import React, { useEffect, useState } from "react";
import { message } from "antd";
import CustomTableWithPagination from "../../../reusable-components/CustomTable";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import {
  getMaterialPriceErrorColumns,
  MaterialPriceErrorTableConsts,
} from "./constants";
import styles from "./MaterialPriceErrorTable.module.scss";
import TNLoader from "../../../reusable-components/TNLoader";
import JobDatabaseService from "../../../services/jobDatabase.service";
import { storageKeys } from "../../../constants/storageKeys";
import { JobFilterOptionPayloadType } from "../../../Types/jobDatabaseTypes";
import { RejectModal } from "./RejectModal";

interface MaterialPriceErrorTableProps {
  jobDatabaseFilters?: JobFilterOptionPayloadType;
  handleAffectedActionComplete?: () => void;
  handleJobProfileNavigate?: () => void;
}

const MaterialPriceErrorTable = ({
  jobDatabaseFilters,
  handleAffectedActionComplete,
  handleJobProfileNavigate
}: MaterialPriceErrorTableProps) => {
  const {
    FetchAffectedMaterialPriceErrorEntries,
    AcceptRevisedPrice,
    RejectRevisedPrice,
  } = JobDatabaseService();
  const {
    search,
    jobNumber,
    jobName,
    companyId,
    materialCategory,
    materialType,
    salesPersonId,
  } = jobDatabaseFilters ?? {};
  const [sortValue, setSortValue] = useState<{
    sortBy?: string;
    sortOrder?: string;
  }>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(
    Number(
      sessionStorage.getItem(storageKeys?.JOB_DB_AFFECTED_ORDERS_PAGESIZE) ??
        10,
    ),
  );
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [selectedRejectRecord, setSelectedRejectRecord] = useState(null);

  const { mutateAsync: acceptRevisedPrice, isPending: isAccepting } =
    AcceptRevisedPrice();
  const { mutateAsync: rejectRevisedPrice, isPending: isRejecting } =
    RejectRevisedPrice();

  const {
    data: affectedJobsResponse,
    isFetching: loading,
    refetch: refetchAffectedJobsResponse,
  } = FetchAffectedMaterialPriceErrorEntries({
    page: currentPage,
    perPage: pageSize,
    search,
    jobNumber: jobNumber ?? [],
    jobName: jobName ?? [],
    companyId: companyId ?? [],
    materialCategory: materialCategory ?? [],
    materialType: materialType ?? [],
    salesPersonId: salesPersonId ?? [],
    sortBy: sortValue?.sortBy,
    sortOrder: sortValue?.sortOrder,
  });

  const data = affectedJobsResponse?.data ?? [];
  const totalCount = affectedJobsResponse?.meta?.totalRecords ?? 0;

  useEffect(() => {
    const storageItem = sessionStorage.getItem(
      storageKeys.JOB_DB_AFFECTED_ORDERS_PAGE,
    );
    if (!!storageItem) {
      const pageValue = JSON.parse(storageItem);
      setCurrentPage(pageValue?.page ?? 1);
    }

    const storageItemSize = sessionStorage.getItem(
      storageKeys.JOB_DB_AFFECTED_ORDERS_PAGESIZE,
    );
    if (!!storageItemSize) {
      setPageSize(Number(storageItemSize));
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    jobNumber,
    jobName,
    companyId,
    materialCategory,
    materialType,
    salesPersonId,
  ]);

  const handleAcceptAction = async (record) => {
    try {
      await acceptRevisedPrice({
        id: record.jobId,
        orderId: record.jobOrderId,
      });
      message.success("Material price updated successfully.");
      handleAffectedActionComplete();
      refetchAffectedJobsResponse();
    } catch (error) {
      message.error("Failed to accept updated price.");
    }
  };

  const handleRejectAction = (record) => {
    setSelectedRejectRecord(record);
    setIsRejectModalVisible(true);
  };

  const handleRejectConfirm = async (payload) => {
    if (!selectedRejectRecord) {
      message.error("No record selected to reject.");
      return;
    }

    try {
      await rejectRevisedPrice({
        id: selectedRejectRecord.jobId,
        orderId: selectedRejectRecord.jobOrderId,
        payload,
      });
      message.success("old price retained");
      setIsRejectModalVisible(false);
      setSelectedRejectRecord(null);
      handleAffectedActionComplete?.();
      refetchAffectedJobsResponse();
    } catch (error) {
      message.error("Failed to submit reject request.");
    }
  };

  const handleRejectCancel = () => {
    setIsRejectModalVisible(false);
    setSelectedRejectRecord(null);
  };

  const handleSorterAction = (sorter: any) => {
    if (!sorter?.order) {
      setSortValue({
        sortBy: undefined,
        sortOrder: undefined,
      });
      return;
    }

    setSortValue({
      sortBy: sorter?.columnKey,
      sortOrder: sorter?.order,
    });
  };

  const handleChange = (page: number) => {
    setCurrentPage(page);
    sessionStorage.setItem(
      storageKeys.JOB_DB_AFFECTED_ORDERS_PAGE,
      JSON.stringify({ page }),
    );
  };

  const handleSizeChangerChange = (size: number) => {
    setCurrentPage(1);
    setPageSize(size);
    try {
      sessionStorage.setItem(storageKeys.JOB_DB_AFFECTED_ORDERS_PAGESIZE, size);
    } catch (err) {
      console.error("Err", err);
    }
  };

  const columns = getMaterialPriceErrorColumns({
    handleAcceptAction,
    handleRejectAction,
    sortBy: sortValue?.sortBy,
    sortOrder: sortValue?.sortOrder,
  });

  return (
    <>
      <TNLoader isLoading={loading || isAccepting || isRejecting} />
      <ConditionalRender
        visible={data?.length > 0}
        fallback={
          <div className={styles.emptyContainer}>
            <p>{MaterialPriceErrorTableConsts.NO_RECORDS}</p>
          </div>
        }
      >
        <CustomTableWithPagination
          data={data}
          columns={columns}
          showPagination={true}
          isSortable={true}
          handleSorter={handleSorterAction}
          rowKey={(record) => record?.jobOrderId}
          loading={loading}
          totalData={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          handleChange={handleChange}
          handleSizeChangerChange={handleSizeChangerChange}
          isClickableTable
          handleSelectedRow={handleJobProfileNavigate}
        />
      </ConditionalRender>
      <RejectModal
        visible={isRejectModalVisible}
        record={selectedRejectRecord}
        onCancel={handleRejectCancel}
        onConfirm={handleRejectConfirm}
      />
    </>
  );
};

export default MaterialPriceErrorTable;
